/**
 * Store list/detail variants: API may send product_objects or child_products.
 */
export const getStoreVariants = (product) => {
    const list = product?.product_objects || product?.child_products || [];
    return Array.isArray(list) ? list : [];
};

/**
 * Nested attribute payload from ProductAttributeValueSerializer (`attribute` or `attribute_object`).
 */
const nestedAttribute = (av) => {
    if (!av) return null;
    if (typeof av.attribute === 'object' && av.attribute) return av.attribute;
    if (typeof av.attribute_object === 'object' && av.attribute_object) return av.attribute_object;
    return null;
};

/**
 * Resolve ProductAttribute dimension id from an attribute_values[] item.
 */
export const resolveAvDimensionId = (av) => {
    if (!av) return null;
    const nested = nestedAttribute(av);
    if (nested?.id != null) return nested.id;
    return av.attribute;
};

/**
 * Non-empty `name` only — empty rows are ignored everywhere.
 */
export const hasUsableAttributeValue = (av) => {
    if (!av || av.name === null || av.name === undefined) return false;
    if (typeof av.name === 'string') return av.name.trim() !== '';
    return true;
};

/**
 * Label = trimmed string value, or null if empty (caller should skip).
 */
export const formatAttributeValueLabel = (av) => {
    if (!hasUsableAttributeValue(av)) return null;
    return typeof av.name === 'string' ? av.name.trim() : String(av.name);
};

/**
 * Selection key = same as display value (non-empty `name` only).
 */
export const attributeValueToken = (av) => formatAttributeValueLabel(av);

/**
 * Map key -> { id: <ProductAttribute pk>, name, key, sequence }
 * Prefer product.attribute_objects; otherwise derive dimensions from product_objects[].attribute_values.
 */
export const buildVariantAttributeContext = (product) => {
    const rawParentAttrs = product?.attribute_objects || [];
    const fromParent = [...(Array.isArray(rawParentAttrs) ? rawParentAttrs : [])]
        .filter((attr) => attr && typeof attr === 'object' && (attr.key || attr.id))
        .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    const attrMap = {};
    if (fromParent.length > 0) {
        fromParent.forEach((attr) => {
            attrMap[attr.key] = {
                id: attr.id,
                name: attr.name,
                key: attr.key,
                sequence: attr.sequence ?? 0,
            };
        });
        return {
            attrs: fromParent.map((a) => ({ ...attrMap[a.key] })),
            attrMap,
        };
    }

    const dimOrder = [];
    const seen = new Set();
    (getStoreVariants(product) || []).forEach((po) => {
        (po.attribute_values || []).forEach((av) => {
            const dimId = resolveAvDimensionId(av);
            if (!dimId || seen.has(String(dimId))) return;
            seen.add(String(dimId));
            const nested = nestedAttribute(av);
            const short =
                nested?.key ||
                `_dim_${String(dimId).replace(/-/g, '').slice(0, 10)}`;
            const name = nested?.name || short;
            dimOrder.push({
                id: dimId,
                key: short,
                name,
                sequence: dimOrder.length,
            });
        });
    });

    const dimsWithValue = new Set();
    (getStoreVariants(product) || []).forEach((po) => {
        (po.attribute_values || []).forEach((av) => {
            if (!hasUsableAttributeValue(av)) return;
            const id = resolveAvDimensionId(av);
            if (id) dimsWithValue.add(String(id));
        });
    });
    const dimOrderFiltered = dimOrder.filter((d) => dimsWithValue.has(String(d.id)));

    dimOrderFiltered.forEach((d) => {
        attrMap[d.key] = {
            id: d.id,
            name: d.name,
            key: d.key,
            sequence: d.sequence,
        };
    });

    return {
        attrs: dimOrderFiltered.map((d) => ({ ...attrMap[d.key] })),
        attrMap,
    };
};

/**
 * Legacy: map from attribute_objects only (no derived dimensions).
 */
export const getAttributeMap = (product) => {
    const { attrMap } = buildVariantAttributeContext(product);
    return attrMap;
};

/**
 * Whether an attribute_values row matches the selected value for this dimension.
 */
const selectionMatchesAv = (av, attrDimId, selectedVal) => {
    if (selectedVal === null || selectedVal === undefined || selectedVal === '') return false;
    if (!hasUsableAttributeValue(av)) return false;
    const dim = resolveAvDimensionId(av);
    if (String(dim) !== String(attrDimId)) return false;
    const t = attributeValueToken(av);
    const s = typeof selectedVal === 'string' ? selectedVal.trim() : String(selectedVal);
    return t != null && String(t) === s;
};

/**
 * Get product_objects that match the current selection.
 * selectedValues: { key: attribute value string }
 */
export const getMatchingProductObjects = (product, selectedValues) => {
    const { attrMap } = buildVariantAttributeContext(product);
    return getMatchingProductObjectsByAttrMap(product, selectedValues, attrMap);
};

/**
 * Match variants using attribute dimension ids from attrMap + non-empty value on each line.
 */
export const getMatchingProductObjectsByAttrMap = (product, selectedValues, attrMap) => {
    const objects = getStoreVariants(product);
    if (!selectedValues || Object.keys(selectedValues).every((k) => !selectedValues[k])) {
        return objects;
    }
    return objects.filter((po) => {
        const avList = po.attribute_values || [];
        for (const [attrShortCode, selectedVal] of Object.entries(selectedValues)) {
            if (!selectedVal) continue;
            const attrInfo = attrMap?.[attrShortCode];
            const attrDimId = attrInfo?.id;
            if (!attrDimId) return false;
            const match = avList.some((a) => selectionMatchesAv(a, attrDimId, selectedVal));
            if (!match) return false;
        }
        return true;
    });
};

/**
 * All distinct options for one dimension: { valueId, label, sequence } (value-only; empty skipped).
 */
export const getAllAttributeValueOptions = (product, attrShortCode, attrMap) => {
    const attrInfo = attrMap?.[attrShortCode];
    const attrDimId = attrInfo?.id;
    if (!attrDimId) return [];
    const byId = new Map();
    (getStoreVariants(product) || []).forEach((po) => {
        (po.attribute_values || []).forEach((av) => {
            const dim = resolveAvDimensionId(av);
            if (String(dim) !== String(attrDimId)) return;
            const token = attributeValueToken(av);
            if (!token) return;
            const label = formatAttributeValueLabel(av);
            if (!label) return;
            const sequence = av.sequence ?? 0;
            if (!byId.has(token)) {
                byId.set(token, { valueId: token, label, sequence });
            }
        });
    });
    return Array.from(byId.values()).sort((a, b) => a.sequence - b.sequence || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
};

/**
 * @deprecated Use getAllAttributeValueOptions — kept for callers expecting raw values[]
 */
export const getAllValuesForAttribute = (product, attrShortCode, attrMap) => {
    return getAllAttributeValueOptions(product, attrShortCode, attrMap).map((o) => o.valueId);
};

/**
 * Exists at least one variant line compatible with this selection if we pick `value` for `attrShortCode`.
 */
export const isValueAvailableForAttribute = (product, attrShortCode, value, attrMap, selectedValues) => {
    const testSelection = { ...selectedValues, [attrShortCode]: value };
    const matching = getMatchingProductObjectsByAttrMap(product, testSelection, attrMap);
    return matching.length > 0;
};

/**
 * Options still valid for this dimension given other selections (constraint propagation).
 */
export const getAvailableValuesForAttribute = (product, attrShortCode, attrMap, selectedValues) => {
    const otherSelections = { ...selectedValues };
    delete otherSelections[attrShortCode];
    const matching = getMatchingProductObjectsByAttrMap(product, otherSelections, attrMap);
    const attrInfo = attrMap?.[attrShortCode];
    const attrDimId = attrInfo?.id;
    if (!attrDimId) return [];
    const byId = new Map();
    matching.forEach((po) => {
        (po.attribute_values || []).forEach((av) => {
            const dim = resolveAvDimensionId(av);
            if (String(dim) !== String(attrDimId)) return;
            const token = attributeValueToken(av);
            if (!token) return;
            const label = formatAttributeValueLabel(av);
            if (!label) return;
            const sequence = av.sequence ?? 0;
            if (!byId.has(token)) {
                byId.set(token, { valueId: token, label, sequence });
            }
        });
    });
    return Array.from(byId.values()).sort((a, b) => a.sequence - b.sequence || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
};

/**
 * Find the single product_object that matches the full selection.
 */
export const getSelectedVariant = (product, selectedValues, attrMap) => {
    const matching = getMatchingProductObjectsByAttrMap(product, selectedValues, attrMap);
    if (matching.length === 1) {
        return { product_object: matching[0], product_number: matching[0].product_number };
    }
    return null;
};

/**
 * After changing one attribute, clear other picks that are no longer part of any valid combination.
 * When `preserveShortCode` is set (the attribute just chosen), that pick is never cleared —
 * conflicting others are dropped instead (e.g. pick size M while color Zwart is selected → clear Zwart).
 */
export const pruneInvalidVariantSelections = (product, attrMap, attrs, selectedValues, preserveShortCode = null) => {
    const next = { ...selectedValues };
    if (preserveShortCode && next[preserveShortCode]) {
        // Only keep other selections that still appear on some variant matching the preserved pick.
        const core = { [preserveShortCode]: next[preserveShortCode] };
        const matching = getMatchingProductObjectsByAttrMap(product, core, attrMap);
        (attrs || []).forEach((a) => {
            const sc = a.key;
            if (sc === preserveShortCode) return;
            const v = next[sc];
            if (!v) return;
            const attrDimId = attrMap?.[sc]?.id;
            if (!attrDimId) {
                delete next[sc];
                return;
            }
            const stillValid = matching.some((po) =>
                (po.attribute_values || []).some((av) => selectionMatchesAv(av, attrDimId, v))
            );
            if (!stillValid) {
                delete next[sc];
            }
        });
        return next;
    }

    let changed = true;
    while (changed) {
        changed = false;
        (attrs || []).forEach((a) => {
            const sc = a.key;
            const v = next[sc];
            if (!v) return;
            if (!isValueAvailableForAttribute(product, sc, v, attrMap, next)) {
                delete next[sc];
                changed = true;
            }
        });
    }
    return next;
};

/**
 * Apply a new attribute pick and invalidate incompatible other attributes.
 */
export const selectVariantAttribute = (product, attrMap, attrs, selectedValues, shortCode, value) => {
    return pruneInvalidVariantSelections(
        product,
        attrMap,
        attrs,
        { ...selectedValues, [shortCode]: value },
        shortCode,
    );
};

/**
 * Default store selection: first available option per attribute (in attr order),
 * keeping a valid combination via selectVariantAttribute.
 */
export const getDefaultVariantSelection = (product, attrMap, attrs) => {
    let selected = {};
    (attrs || []).forEach((attr) => {
        const options = getAllAttributeValueOptions(product, attr.key, attrMap);
        if (!options.length) return;
        const pick =
            options.find((opt) =>
                isValueAvailableForAttribute(product, attr.key, opt.valueId, attrMap, selected)
            ) || options[0];
        if (!pick?.valueId) return;
        selected = selectVariantAttribute(
            product,
            attrMap,
            attrs,
            selected,
            attr.key,
            pick.valueId,
        );
    });
    return selected;
};

/**
 * Derive attribute filters from products for the sidebar.
 */
export const deriveAttributeFiltersFromProducts = (products) => {
    const attrMap = {};
    const valueToAttr = {};
    const allCombos = [];

    (products || []).forEach((p) => {
        const { attrs } = buildVariantAttributeContext(p);
        attrs.forEach((a) => {
            attrMap[a.key] = { id: a.id, name: a.name, key: a.key };
        });
        const objects = getStoreVariants(p);
        objects.forEach((po) => {
            const avList = po.attribute_values || [];
            const combo = [];
            avList.forEach((a) => {
                const attrId = resolveAvDimensionId(a);
                const sc = Object.entries(attrMap).find(([_, info]) => String(info.id) === String(attrId))?.[0];
                const token = attributeValueToken(a);
                if (sc && token) {
                    combo.push({ key: sc, value: token });
                    if (!valueToAttr[sc]) valueToAttr[sc] = new Set();
                    valueToAttr[sc].add(token);
                }
            });
            if (combo.length) allCombos.push(combo);
        });
    });

    const filters = {};
    Object.entries(attrMap).forEach(([sc, info]) => {
        filters[sc] = {
            label: info.name,
            key: sc,
            type: 'choice',
            choices: Array.from(valueToAttr[sc] || []).map((v) => ({ value: v, display: v })),
            allCombos,
        };
    });
    return { filters, attrMap };
};

/**
 * Given current filter selection and attribute filters, return available options per attribute.
 */
export const getFilterOptionsForAttribute = (attrShortCode, attributeFilters, filterValues) => {
    const config = attributeFilters?.[attrShortCode];
    if (!config || !config.choices) return [];
    const otherSelections = {};
    Object.keys(attributeFilters || {}).forEach((k) => {
        if (k !== attrShortCode && filterValues[k]) otherSelections[k] = filterValues[k];
    });
    const hasOther = Object.values(otherSelections).some(Boolean);
    if (!hasOther) return config.choices;
    const combos = config.allCombos || [];
    const validValues = new Set();
    combos.forEach((combo) => {
        let match = true;
        for (const [k, v] of Object.entries(otherSelections)) {
            if (!v) continue;
            const found = combo.find((c) => c.key === k && String(c.value) === String(v));
            if (!found) {
                match = false;
                break;
            }
        }
        if (match) {
            const val = combo.find((c) => c.key === attrShortCode)?.value;
            if (val != null && val !== '') validValues.add(val);
        }
    });
    return config.choices.filter((c) => validValues.has(c.value));
};
